import NotificationActions from 'src/stores/alt/actions/NotificationActions';
import {
  DEFAULT_DASHBOARD_READY_TIMEOUT_MS,
  DEFAULT_DASHBOARD_READY_POLL_INTERVAL_MS,
} from './dashboardFeatureConfig';
import { getDashboardUserContext } from './dashboardUserContext';
import {
  DASHBOARD_COMPONENT_NAME,
  DASHBOARD_CONFIG_WINDOW_KEY,
  DASHBOARD_EVENT_AGGREGATE,
  DASHBOARD_EVENT_OPEN_CLICKED,
  DASHBOARD_EVENT_OPEN_READY,
  DASHBOARD_EVENT_OPENED,
  DASHBOARD_EVENT_OPEN_FAILED,
  DASHBOARD_EVENT_CLOSED,
} from './dashboard';

const OPEN_FAILED_REASONS = {
  DISABLED: 'disabled',
  ALREADY_OPEN: 'already-open',
  RUNTIME_NOT_READY_TIMEOUT: 'runtime-not-ready-timeout',
  RUNTIME_MOUNT_MISSING: 'runtime-mount-missing',
  MOUNT_ERROR: 'mount-error',
};

function getAddonRuntime() {
  return window.Addon;
}

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isDashboardEnabled() {
  return window[DASHBOARD_CONFIG_WINDOW_KEY]?.enabled === true;
}

function emitDashboardEvent(eventName, payload) {
  const detail = {
    event: eventName,
    ts: Date.now(),
    source: 'topbar',
    ...payload,
  };

  window.dispatchEvent(new CustomEvent(eventName, { detail }));
  window.dispatchEvent(new CustomEvent(DASHBOARD_EVENT_AGGREGATE, { detail }));
  // eslint-disable-next-line no-console
  console.log('[dashboard-event]', eventName, detail);
}

function showUnavailableNotification() {
  NotificationActions.add({
    title: 'Dashboard unavailable',
    message: 'Dashboard could not be opened right now. Please try again.',
    level: 'warning',
    position: 'tr',
    dismissible: 'button',
    autoDismiss: 5,
  });
}

export function createDashboardFeature(options = {}) {
  const {
    onOpenStateChange,
    onRequestClose,
    readyTimeoutMs = DEFAULT_DASHBOARD_READY_TIMEOUT_MS,
    readyPollIntervalMs = DEFAULT_DASHBOARD_READY_POLL_INTERVAL_MS,
  } = options;

  let mountNodeRef = null;
  let mountHost = null;
  let addonInstance = null;
  let openerEl = null;
  let isOpen = false;
  let isOpening = false;
  let lastUserId = null;

  const notifyOpenState = () => {
    onOpenStateChange?.({ isOpen, isOpening });
  };

  const close = () => {
    if (addonInstance && typeof addonInstance.unmount === 'function') {
      addonInstance.unmount();
    }
    addonInstance = null;
    mountNodeRef = null;
    if (mountHost && mountHost.parentNode) {
      mountHost.parentNode.removeChild(mountHost);
    }
    mountHost = null;

    const wasOpen = isOpen;
    isOpen = false;
    isOpening = false;

    if (wasOpen) {
      emitDashboardEvent(DASHBOARD_EVENT_CLOSED, { userId: lastUserId, outcome: 'closed' });
    }

    notifyOpenState();
    if (openerEl instanceof HTMLElement) {
      openerEl.focus();
    }
    openerEl = null;
  };

  const waitForDashboardReady = async () => {
    const startedAt = Date.now();

    while (Date.now() - startedAt <= readyTimeoutMs) {
      const runtime = getAddonRuntime();
      if (
        runtime
        && typeof runtime.has === 'function'
        && typeof runtime.mount === 'function'
        && runtime.has(DASHBOARD_COMPONENT_NAME)
      ) {
        return true;
      }

      // eslint-disable-next-line no-await-in-loop
      await sleep(readyPollIntervalMs);
    }

    return false;
  };

  const open = async ({ openerEl: providedOpenerEl }) => {
    const { userId, displayName } = getDashboardUserContext();

    if (!isDashboardEnabled()) {
      return { ok: false, reason: OPEN_FAILED_REASONS.DISABLED };
    }

    if (isOpen || isOpening) {
      return { ok: false, reason: OPEN_FAILED_REASONS.ALREADY_OPEN };
    }

    lastUserId = userId;
    openerEl = providedOpenerEl ?? null;
    emitDashboardEvent(DASHBOARD_EVENT_OPEN_CLICKED, { userId });
    isOpening = true;
    notifyOpenState();

    const ready = await waitForDashboardReady();
    if (!ready) {
      emitDashboardEvent(DASHBOARD_EVENT_OPEN_FAILED, {
        userId,
        outcome: 'failed',
        reason: OPEN_FAILED_REASONS.RUNTIME_NOT_READY_TIMEOUT,
      });
      showUnavailableNotification();
      isOpening = false;
      notifyOpenState();
      return { ok: false, reason: OPEN_FAILED_REASONS.RUNTIME_NOT_READY_TIMEOUT };
    }

    emitDashboardEvent(DASHBOARD_EVENT_OPEN_READY, { userId, outcome: 'ready' });

    const runtime = getAddonRuntime();
    if (!runtime || typeof runtime.mount !== 'function') {
      emitDashboardEvent(DASHBOARD_EVENT_OPEN_FAILED, {
        userId,
        outcome: 'failed',
        reason: OPEN_FAILED_REASONS.RUNTIME_MOUNT_MISSING,
      });
      showUnavailableNotification();
      isOpening = false;
      notifyOpenState();
      return { ok: false, reason: OPEN_FAILED_REASONS.RUNTIME_MOUNT_MISSING };
    }

    if (!mountNodeRef) {
      mountHost = document.createElement('div');
      mountHost.setAttribute('data-addon-slot', DASHBOARD_COMPONENT_NAME);
      mountHost.setAttribute('data-chemkit-dashboard-root', 'true');
      document.body.appendChild(mountHost);
      mountNodeRef = mountHost;
    }

    try {
      addonInstance = runtime.mount(
        DASHBOARD_COMPONENT_NAME,
        mountNodeRef,
        {
          userId,
          displayName,
          onRequestClose: () => {
            onRequestClose?.();
            close();
          },
        },
        { shadowDom: true },
      );
      isOpen = true;
      isOpening = false;
      notifyOpenState();
      emitDashboardEvent(DASHBOARD_EVENT_OPENED, { userId, outcome: 'opened' });
      return { ok: true };
    } catch (_error) {
      emitDashboardEvent(DASHBOARD_EVENT_OPEN_FAILED, {
        userId,
        outcome: 'failed',
        reason: OPEN_FAILED_REASONS.MOUNT_ERROR,
      });
      showUnavailableNotification();
      isOpen = false;
      isOpening = false;
      notifyOpenState();
      return { ok: false, reason: OPEN_FAILED_REASONS.MOUNT_ERROR };
    }
  };

  return {
    isEnabled: isDashboardEnabled,
    open,
    close,
  };
}
